import { motion } from 'framer-motion';
import { Save } from 'lucide-react';

interface Question {
  id: string;
  type: string;
  title: string;
  score: number;
  minTextLength?: number;
  maxTextLength?: number;
}

interface SubjectiveTextAnswerAreaProps {
  question: Question;
  answer: string;
  isRevealed: boolean;
  isSaving?: boolean;
  onTextAnswerChange: (questionId: string, text: string) => void;
  onSaveAnswer: (question: Question) => void;
}

export default function SubjectiveTextAnswerArea({
  question,
  answer,
  isRevealed,
  isSaving = false,
  onTextAnswerChange,
  onSaveAnswer,
}: SubjectiveTextAnswerAreaProps) {
  const hasContent = (answer || '').trim().length > 0;

  // 生成字数限制提示文本
  const getWordLimitText = () => {
    const max = question.maxTextLength || 0;
    const min = question.minTextLength || 0;
    if (max === 0 && min === 0) return '不限字数';
    if (max === 0) return `作答不少于 ${min} 字`;
    if (min === 0) return `作答不超过 ${max} 字`;
    return `作答 ${min} - ${max} 字`;
  };

  return (
    <div className="space-y-4">
      {/* 文本输入区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="p-4">
          <textarea
            value={answer}
            onChange={(e) => onTextAnswerChange(question.id, e.target.value)}
            disabled={isRevealed}
            placeholder="请输入你的答案..."
            className={`w-full min-h-[200px] p-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y ${
              isRevealed ? 'bg-gray-50 cursor-not-allowed' : ''
            }`}
          />

          {/* 保存作答按钮 */}
          {!isRevealed && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {getWordLimitText()}
              </p>
              <button
                onClick={() => onSaveAnswer(question)}
                disabled={!hasContent || isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed disabled:shadow-none"
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? '保存中...' : '保存作答'}</span>
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
